/* eslint-disable prettier/prettier */

/**
 * @filedescription Object Schema
 */
// -----------------------------------------------------------------------------
// Requirements
// -----------------------------------------------------------------------------
import MergeStrategy from './mergeStrategy';
import ValidationStrategy from './validationStrategy';

// -----------------------------------------------------------------------------
// Private
// -----------------------------------------------------------------------------

const strategies = Symbol('strategies');
const requiredKeys = Symbol('requiredKeys');

/**
 * Validates a schema strategy.
 * @param {string} name The name of the key this strategy is for.
 * @param {Object} strategy The strategy for the object key.
 * @param {boolean} [strategy.required=true] Whether the key is required.
 * @param {string[]} [strategy.requires] Other keys that are required when
 *      this key is present.
 * @param {Function} strategy.merge A method to call when merging two objects
 *      with the same key.
 * @param {Function} strategy.validate A method to call when validating an
 *      object with the key.
 * @returns {void}
 * @throws {Error} When the strategy is missing a name.
 * @throws {Error} When the strategy is missing a merge() method.
 * @throws {Error} When the strategy is missing a validate() method.
 */
function validateDefinition(name, strategy) {
  let hasSchema = false;
  if (strategy.schema) {
    if (typeof strategy.schema === 'object') {
      hasSchema = true;
    } else {
      throw new TypeError('Schema must be an object.');
    }
  }

  if (typeof strategy.merge === 'string') {
    if (!(strategy.merge in MergeStrategy)) {
      throw new TypeError(
        `Definition for key "${name}" missing valid merge strategy.`
      );
    }
  } else if (!hasSchema && typeof strategy.merge !== 'function') {
    throw new TypeError(
      `Definition for key "${name}" must have a merge property.`
    );
  }

  if (typeof strategy.validate === 'string') {
    if (!(strategy.validate in ValidationStrategy)) {
      throw new TypeError(
        `Definition for key "${name}" missing valid validation strategy.`
      );
    }
  } else if (!hasSchema && typeof strategy.validate !== 'function') {
    throw new TypeError(
      `Definition for key "${name}" must have a validate() method.`
    );
  }
}

// -----------------------------------------------------------------------------
// Class
// -----------------------------------------------------------------------------

interface IMergeSchema {
  definitions: Record<string, any>;
}

/**
 * Represents an object validation/merging schema.
 */
export default class MergeSchema implements IMergeSchema {
  /**
   * Creates a new instance.
   */
  definitions: Record<string, any>;
  constructor(definitions) {
    if (!definitions) {
      throw new Error('Schema definitions missing.');
    }

    this.definitions = JSON.parse(JSON.stringify(definitions)); // TODO: fix deep copy

    /**
     * Track all strategies in the schema by key.
     * @type {Map}
     * @property strategies
     */
    this[strategies] = new Map();

    /**
     * Separately track any keys that are required for faster validation.
     * @type {Map}
     * @property requiredKeys
     */
    this[requiredKeys] = new Map();

    // add in all strategies
    for (const key of Object.keys(this.definitions)) {
      validateDefinition(key, this.definitions[key]);

      // normalize merge and validate methods if subschema is present
      if (typeof this.definitions[key].schema === 'object') {
        const merge =
          this.definitions[key]?.merge !== undefined
            ? MergeStrategy[this.definitions[key].merge]
            : null;
        const schema = new MergeSchema(this.definitions[key].schema);
        this.definitions[key] = {
          ...this.definitions[key],
          merge: function(first = {}, second = {}) {
            const result = merge ? merge(first, second) : {};
            return Object.assign(result, schema.merge(first, second));
          },
          validate: function(value) {
            ValidationStrategy.object(value);
            schema.validate(value);
          },
        };
      }

      // normalize the merge method in case there's a string
      if (typeof this.definitions[key].merge === 'string') {
        this.definitions[key] = {
          ...this.definitions[key],
          merge: MergeStrategy[this.definitions[key].merge],
        };
      }

      // normalize the validate method in case there's a string
      if (typeof this.definitions[key].validate === 'string') {
        this.definitions[key] = {
          ...this.definitions[key],
          validate: ValidationStrategy[this.definitions[key].validate],
        };
      }

      this[strategies].set(key, this.definitions[key]);

      if (this.definitions[key].required) {
        this[requiredKeys].set(key, this.definitions[key]);
      }
    }
  }

  /**
   * Determines if a strategy has been registered for the given object key.
   * @param {string} key The object key to find a strategy for.
   * @returns {boolean} True if the key has a strategy registered, false if not.
   */
  hasKey(key) {
    return this[strategies].has(key);
  }

  /**
   * Merges objects together to create a new object comprised of the keys
   * of the all objects. Keys are merged based on the each key's merge
   * strategy.
   * @param {...Object} objects The objects to merge.
   * @returns {Object} A new object with a mix of all objects' keys.
   * @throws {Error} If any object is invalid.
   */
  merge(...objects) {
    // double check arguments
    if (objects.length < 2) {
      throw new Error('merge() requires at least two arguments.');
    }

    if (objects.some(object => object == null || typeof object !== 'object')) {
      throw new Error('All arguments must be objects.');
    }

    return objects.reduce((result, object) => {
      this.validate(object);

      for (const [key, strategy] of this[strategies]) {
        try {
          if (key in result || key in object) {
            const value = strategy.merge.call(this, result[key], object[key]);
            if (value !== undefined) {
              result[key] = value;
            }
          }
        } catch (ex) {
          ex.message = `Key "${key}": ` + ex.message;
          throw ex;
        }
      }
      return result;
    }, {});
  }

  /**
   * Validates an object's keys based on the validate strategy for each key.
   * @param {Object} object The object to validate.
   * @returns {void}
   * @throws {Error} When the object is invalid.
   */
  validate(object) {
    // check existing keys first
    for (const key of Object.keys(object)) {
      // check to see if the key is defined
      if (!this.hasKey(key)) {
        // TODO
        // throw new Error(`Unexpected key "${key}" found.`);
        continue;
      }

      // validate existing keys
      const strategy = this[strategies].get(key);
      const objectKey = object[key];
      // first check to see if any other keys are required
      if (Array.isArray(strategy.requires)) {
        if (!strategy.requires.every(otherKey => otherKey in objectKey)) {
          throw new Error(
            `Key "${key}" requires keys "${strategy.requires.join('", "')}".`
          );
        }
      }

      // now apply remaining validation strategy
      try {
        // eslint-disable-next-line no-useless-call
        strategy.validate.call(strategy, objectKey);
      } catch (ex) {
        ex.message = `Key "${key}": ` + ex.message;
        throw ex;
      }
    }

    // ensure required keys aren't missing
    // TODO
    // при обработки вложенной schema
    if (Object.keys(object).length) {
      for (const [key] of this[requiredKeys]) {
        if (!(key in object)) {
          throw new Error(`Missing required key "${key}".`);
        }
      }
    }
  }
}

export { MergeSchema };
